// libs
import { useAuthLogin } from '@lopo-tech/lopo-api'
import React, { useEffect, useRef } from 'react'

// interfaces / constants
import { CLASS_PREFIX } from 'src/constants/'
import {
  GTM_ACTION_BANNER_BLOCKER,
  GTM_CATEGORY_BANNERBLOCKER
} from 'src/constants/google_tag_manager'

// components
import { Button } from 'src/components/button/button'

// helpers
import { useShowRegistrationModal } from 'src/actions/app-state/app-state_hooks'
import { textResources } from 'src/lang/de'
import { useIsModalOpen } from 'src/reducers/app-state/app-state_hooks'
// The telemetry identifiers deliberately keep the old banner's name: renaming them would
// start a new time series and cut the reporting off from everything before this component.
// The component was renamed, the events were not.
import initTracker, { COLLAPSED_BANNER } from 'src/utils/reporting/events_tracking/events_tracking'
import GoogleTagManager from 'src/utils/reporting/google-tag-manager'
import { useSignupCtaSpace } from './use_signup_cta_space'

// styles
import './signup_cta.scss'

const cls = `${CLASS_PREFIX}signup-cta`
const labels = textResources.authentication
const signupCtaTracker = initTracker(COLLAPSED_BANNER)

/**
 * Persistent call to action for logged out visitors: a full width bar at the bottom of the
 * viewport on mobile, a floating pill in the lower centre on desktop. Both states are this one
 * component; only the message differs, so an instance can word them independently.
 */
const SignupCta: React.FC = () => {
  const { isAuthenticated } = useAuthLogin()
  const isModalOpen = useIsModalOpen()
  const showRegistrationModal = useShowRegistrationModal()
  const element = useRef<HTMLDivElement>(null)
  const hasTracked = useRef(false)

  // the login / registration modal is the CTA's own target, so the CTA steps aside while it is open
  const visible = !isAuthenticated && !isModalOpen

  useSignupCtaSpace(element, visible)

  useEffect(() => {
    if (!visible || hasTracked.current) { return }

    hasTracked.current = true
    GoogleTagManager.pushEventVariable(GTM_CATEGORY_BANNERBLOCKER, GTM_ACTION_BANNER_BLOCKER)
    signupCtaTracker(COLLAPSED_BANNER.ACTIONS.SHOW, COLLAPSED_BANNER.LABELS.START)
  }, [visible])

  if (!visible) { return null }

  return (
    <div className={cls} ref={element}>
      <span className={`${cls}__message ${cls}__message--mobile`}>{labels.registerLopo}</span>
      <span className={`${cls}__message ${cls}__message--desktop`}>{labels.registerNow}</span>
      <Button
        onClick={showRegistrationModal}
        size={'small'}
        title={textResources.shared.register}
      />
    </div>
  )
}

export default SignupCta
